import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react";
import cn from "classnames";
import { DuoParticipant, Group } from "../../../../Duo.types";
import { defineMessages, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import useTeacherDuo from "../../../useTeacherDuo";

import "./ChooseDuo.scss";
import Avatar from "../../../../../../../components/Avatar/Avatar";

type Props = {
    group: Group;
    setDuo: Dispatch<
        SetStateAction<{
            mentee?: DuoParticipant;
            mentor?: DuoParticipant;
        }>
    >;
    mentee?: DuoParticipant;
    mentor?: DuoParticipant;
};

const ChooseDuo = ({ group, setDuo, mentee, mentor }: Props) => {
    const intl = useIntl();
    const { resourceId } = useParams<{ resourceId: string }>();
    const { getGroupStudents } = useTeacherDuo();

    const students = useMemo(
        () => getGroupStudents(group.id, resourceId),
        [getGroupStudents, group.id, resourceId]
    );

    // First item in array is the mentee, second is the mentor
    const [checked, setChecked] = useState<string[]>(
        initChecked(mentee, mentor)
    );

    // Set participants when duo is completed / uncompleted
    useEffect(() => {
        if (checked.length !== 2 && (mentor || mentee))
            setDuo((duo) => ({ ...duo, mentor: undefined, mentee: undefined }));
        if (checked.length === 2)
            setDuo((duo) => ({
                ...duo,
                mentee: students.find((student) => student.id === checked[0]),
                mentor: students.find((student) => student.id === checked[1]),
            }));
    }, [checked, mentee, mentor, setDuo, students]);

    const handleCheck = (id: string) => () => {
        setChecked((checked) => {
            if (checked.includes(id))
                return checked.filter((checked) => checked !== id);
            if (checked.length < 2) return [...checked, id];
            return [checked[1], id];
        });
    };

    return (
        <fieldset className="choose-duo__container">
            <h4>{group.name}</h4>
            <div className="choose-duo__students-list">
                {students.map((student) => (
                    <div
                        className={cn("choose-duo__student", {
                            "--selected": checked.includes(student.id),
                            "--uneligible-student":
                                student.alreadyInDuo ||
                                !student.preliminaryTestCompleted,
                        })}
                        key={student.id}
                    >
                        <label htmlFor={student.id}>
                            <Avatar
                                name={`${student.firstname} ${student.lastname}`}
                                colors={{
                                    background:
                                        "var(--element-background-avatar)",
                                    text: "var(--text-main-inverted)",
                                }}
                            />
                            <div className="choose-duo__student-name">
                                <span>
                                    {student.firstname} {student.lastname}
                                </span>
                                {student.alreadyInDuo &&
                                    student.preliminaryTestCompleted && (
                                        <span className="choose-student__uneligible-message">
                                            {intl.formatMessage(
                                                messages.studentAlreadyInDuo
                                            )}
                                        </span>
                                    )}
                                {!student.preliminaryTestCompleted && (
                                    <span className="choose-student__uneligible-message">
                                        {intl.formatMessage(
                                            messages.studentHasNotDoneTest
                                        )}
                                    </span>
                                )}
                            </div>
                        </label>
                        <input
                            id={student.id}
                            type="checkbox"
                            onChange={handleCheck(student.id)}
                            checked={checked.includes(student.id)}
                            disabled={
                                student.alreadyInDuo ||
                                !student.preliminaryTestCompleted
                            }
                        />
                    </div>
                ))}
            </div>
        </fieldset>
    );
};
export default ChooseDuo;

const initChecked = (
    mentee: { id: string } | undefined,
    mentor: { id: string } | undefined
) => {
    if (mentee && mentor) return [mentee.id, mentor.id];
    return [];
};

const messages = defineMessages({
    studentAlreadyInDuo: {
        id: "workmode-duo-studentAlreadyInDuo",
        defaultMessage: "The student is already in a duo for this module",
    },
    studentHasNotDoneTest: {
        id: "workmode-duo-studentHasNotDoneTest",
        defaultMessage:
            "The  student needs to complete the initial test and start the module before you can add them to a duo",
    },
});
